/*!
 * Raceboardblade 2027 — Reservation interest modal
 * Opens on #reservation hash and CTAs linking to #reservation.
 * Captures product ID from range card CTAs into Klaviyo profile properties.
 */
(function () {
  'use strict';

  var HASH = '#reservation';
  var STORAGE_KEY = 'rbb-2027-waitlist';
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var productContext = null;
  var boundModal = null;
  var lifecycle = null;
  var focusTimer = null;
  var previousFocus = null;
  var previousOverflow = null;

  function getModal() {
    return document.querySelector('[data-rbb-reservation-modal]');
  }

  function isOpen(modal) {
    return modal && modal.classList.contains('rbb-res-modal--open');
  }

  function readStoredSuccess() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return false;
      var data = JSON.parse(raw);
      return !!(data && data.email);
    } catch (err) {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    }
  }

  function storeSuccess(email) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ email: email, ts: Date.now() }));
    } catch (err) {
      try { localStorage.setItem(STORAGE_KEY, 'true'); } catch (e) {}
    }
  }

  function readContextFromElement(el) {
    if (!el) return null;

    var card = el.closest('.rbb-product-card');
    var source = card || el;

    var id = source.getAttribute('data-rbb-interest-product-id') || '';
    var handle = source.getAttribute('data-rbb-interest-product-handle') || '';
    var name = source.getAttribute('data-rbb-interest-product-name') || '';
    var cardId = source.getAttribute('data-rbb-interest-card-id') || '';

    if (!id && !handle && !name && !cardId) return null;

    return { id: id, handle: handle, name: name, cardId: cardId };
  }

  function applyProductContext(modal, context) {
    productContext = context || null;

    var contextEl = modal.querySelector('[data-rbb-res-modal-product-context]');
    if (contextEl) {
      if (context && context.name) {
        contextEl.textContent = context.name;
        contextEl.classList.add('rbb-res-modal__product-context--visible');
        contextEl.hidden = false;
      } else {
        contextEl.textContent = '';
        contextEl.classList.remove('rbb-res-modal__product-context--visible');
        contextEl.hidden = true;
      }
    }

    modal.setAttribute('data-rbb-active-product-id', context && context.id ? context.id : '');
  }

  function buildKlaviyoIdentifyProps(modal, email, fields) {
    var productProp = modal.getAttribute('data-klaviyo-product-property') || 'Product ID';
    var modelProp = modal.getAttribute('data-klaviyo-model-property') || 'Interested Model';
    var props = { $email: email };

    if (fields.firstName) props.$first_name = fields.firstName;
    if (fields.lastName) props.$last_name = fields.lastName;
    if (fields.country) props.Country = fields.country;

    if (productContext) {
      if (productContext.id) props[productProp] = productContext.id;
      if (productContext.name) props[modelProp] = productContext.name;
      if (productContext.handle) props['Product Handle'] = productContext.handle;
      if (productContext.cardId) props['Interest Card ID'] = productContext.cardId;
    }

    return props;
  }

  function buildKlaviyoListProps(fields, context) {
    var props = {};
    if (fields.country) props.Country = fields.country;
    if (context) {
      if (context.id) props['Product ID'] = context.id;
      if (context.name) props['Interested Model'] = context.name;
      if (context.handle) props['Product Handle'] = context.handle;
      if (context.cardId) props['Interest Card ID'] = context.cardId;
    }
    return props;
  }

  function bindKlaviyoSubmitListener(modal) {
    if (modal.klaviyoListenerBound) return;

    var formId = modal.getAttribute('data-klaviyo-form-id');
    if (!formId) return;

    window.addEventListener('klaviyoForms', function (e) {
      if (!e.detail || e.detail.type !== 'submit') return;
      if (e.detail.formId !== formId) return;

      var email = e.detail.metaData && (e.detail.metaData.$email || e.detail.metaData.email);
      if (!email || !window.LoftsailsKlaviyo) return;

      var props = buildKlaviyoIdentifyProps(modal, email, {});
      window.LoftsailsKlaviyo.identifyProfile(props);
    }, { signal: lifecycle.signal });

    modal.klaviyoListenerBound = true;
  }

  function openModal(modal, context) {
    if (!modal || isOpen(modal)) return;

    previousFocus = document.activeElement;
    previousOverflow = document.body.style.overflow;
    applyProductContext(modal, context);

    modal.classList.add('rbb-res-modal--open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    var closeBtn = modal.querySelector('[data-rbb-res-modal-close]');
    if (closeBtn) {
      clearTimeout(focusTimer);
      focusTimer = setTimeout(function () { if (isOpen(modal)) closeBtn.focus(); }, 100);
    }

    var klaviyoEmbed = modal.querySelector('.rbb-res-modal__klaviyo');
    if (klaviyoEmbed && window._klOnsite) {
      window._klOnsite.push(['refreshDynamicContent']);
    }

    bindKlaviyoSubmitListener(modal);

    if (window.location.hash !== HASH) {
      history.replaceState(null, '', HASH);
    }
  }

  function closeModal(modal) {
    if (!modal || !isOpen(modal)) return;

    clearTimeout(focusTimer);
    previousFocus?.focus({ preventScroll: true });
    previousFocus = null;
    modal.classList.remove('rbb-res-modal--open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = previousOverflow || '';
    previousOverflow = null;

    if (window.location.hash === HASH) {
      history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }

  function isReservationLink(el) {
    if (!el || el.tagName !== 'A') return false;
    var href = el.getAttribute('href') || '';
    return href === HASH || href.endsWith(HASH);
  }

  function handleFocusTrap(e, modal) {
    if (!isOpen(modal) || e.key !== 'Tab') return;

    var sheet = modal.querySelector('[data-rbb-res-modal-sheet]');
    if (!sheet) return;

    var focusable = sheet.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

    if (!focusable.length) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function initForm(modal) {
    var strings = JSON.parse(modal.dataset.formStrings);
    var formId = modal.getAttribute('data-rbb-res-modal-form-id');
    var form = formId ? document.getElementById(formId) : null;
    if (!form) return;

    var formWrap = modal.querySelector('[data-rbb-res-modal-form-wrap]');
    var successEl = modal.querySelector('[data-rbb-res-modal-success]');
    var errorEl = modal.querySelector('[data-rbb-res-modal-error]');
    var submitBtn = modal.querySelector('[data-rbb-res-modal-submit]');
    var fnInput = modal.querySelector('[data-rbb-res-modal-firstname]');
    var lnInput = modal.querySelector('[data-rbb-res-modal-lastname]');
    var emInput = modal.querySelector('[data-rbb-res-modal-email]');
    var coInput = modal.querySelector('[data-rbb-res-modal-country]');

    if (coInput && coInput.options.length) coInput.selectedIndex = 0;

    function showError(msg) {
      if (!errorEl) return;
      errorEl.textContent = msg;
      errorEl.classList.add('rbb-res-modal__error--visible');
    }

    function hideError() {
      if (errorEl) errorEl.classList.remove('rbb-res-modal__error--visible');
    }

    function showSuccess() {
      if (formWrap) formWrap.style.display = 'none';
      if (successEl) successEl.classList.add('rbb-res-modal__success--visible');
    }

    if (readStoredSuccess()) showSuccess();

    var klaviyoListId = modal.getAttribute('data-klaviyo-list-id');
    var klaviyoCompanyId = modal.getAttribute('data-klaviyo-public-key');
    var useKlaviyoList = !!(klaviyoListId && klaviyoCompanyId && window.LoftsailsKlaviyo);

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      hideError();
      applyProductContext(modal, productContext);

      var firstName = (fnInput && fnInput.value || '').trim();
      var lastName = (lnInput && lnInput.value || '').trim();
      var email = (emInput && emInput.value || '').trim();
      var country = (coInput && coInput.value || '').trim();

      if (!firstName) {
        showError(strings.first_name);
        if (fnInput) fnInput.focus();
        return;
      }
      if (!lastName) {
        showError(strings.last_name);
        if (lnInput) lnInput.focus();
        return;
      }
      if (!EMAIL_RE.test(email)) {
        showError(strings.email);
        if (emInput) emInput.focus();
        return;
      }
      if (!country) {
        showError(strings.country);
        if (coInput) coInput.focus();
        return;
      }

      if (!useKlaviyoList) {
        showError(strings.unavailable);
        return;
      }

      if (submitBtn) submitBtn.disabled = true;

      var fields = {
        email: email,
        firstName: firstName,
        lastName: lastName,
        country: country
      };

      window.LoftsailsKlaviyo.subscribeToList(
        klaviyoCompanyId,
        klaviyoListId,
        {
          email: email,
          firstName: firstName,
          lastName: lastName,
          properties: buildKlaviyoListProps(fields, productContext)
        },
        { source: 'RBB 2027 landing' }
      ).then(function () {
        window.LoftsailsKlaviyo.identifyProfile(buildKlaviyoIdentifyProps(modal, email, fields));
        showSuccess();
        storeSuccess(email);
      }).catch(function (err) {
        console.error('RBB modal Klaviyo error:', err);
        if (submitBtn) submitBtn.disabled = false;
        showError(strings.error);
      });
    });
  }

  function init() {
    var modal = getModal();
    if (!modal || modal === boundModal) return;
    lifecycle?.abort();
    lifecycle = new AbortController();
    boundModal = modal;
    var section = modal.closest('.shopify-section');
    section?.addEventListener('shopify:section:unload', function () {
      closeModal(modal); lifecycle.abort(); boundModal = null;
    }, { signal: lifecycle.signal });

    var closeBtn = modal.querySelector('[data-rbb-res-modal-close]');
    var backdrop = modal.querySelector('[data-rbb-res-modal-backdrop]');

    if (closeBtn) {
      closeBtn.addEventListener('click', function () { closeModal(modal); });
    }

    if (backdrop) {
      backdrop.addEventListener('click', function () { closeModal(modal); });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen(modal)) closeModal(modal);
      handleFocusTrap(e, modal);
    }, { signal: lifecycle.signal });

    document.addEventListener('click', function (e) {
      var link = e.target.closest('a');
      if (!isReservationLink(link)) return;
      e.preventDefault();
      openModal(modal, readContextFromElement(link));
    }, { signal: lifecycle.signal });

    window.addEventListener('hashchange', function () {
      if (window.location.hash === HASH) {
        openModal(modal, null);
      } else if (isOpen(modal)) {
        closeModal(modal);
      }
    }, { signal: lifecycle.signal });

    if (window.location.hash === HASH) openModal(modal, null);

    initForm(modal);
    bindKlaviyoSubmitListener(modal);

    window.RbbReservationModal = {
      open: function (context) { openModal(modal, context || null); },
      close: function () { closeModal(modal); },
      setProductContext: function (context) { applyProductContext(modal, context); }
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
